import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Copy, QrCode } from "lucide-react";

export default function QRModal({ open, onClose, onSuccess, wallet, defaultTab = "receive" }) {
  const { t } = useApp();
  const [tab, setTab] = useState(defaultTab);
  const [handle, setHandle] = useState("");
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { if (open) setTab(defaultTab); }, [open, defaultTab]);

  const qrValue = wallet ? `sbpay://pay?to=${wallet.handle}` : "";

  const copyHandle = () => {
    navigator.clipboard.writeText("@" + wallet?.handle);
    toast.success(t.toastHandleCopied);
  };

  const pay = async () => {
    let target = handle.trim();
    const m = target.match(/to=([^&]+)/);
    if (m) target = m[1];
    const amt = parseFloat(amount);
    if (!target || !amt || amt <= 0) return;
    setLoading(true);
    try {
      const res = await api.post("/wallet/send", { recipient: target, amount: amt, note: "QR" });
      toast.success(`${t.toastPaid} · ${res.data.recipient}`);
      setHandle(""); setAmount("");
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || t.errorGeneric);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="qr-code-section" className="max-w-sm rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary" /> {t.myQr}
          </DialogTitle>
          <DialogDescription>{t.qrReceiveDesc}</DialogDescription>
        </DialogHeader>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="grid grid-cols-2 w-full rounded-2xl">
            <TabsTrigger data-testid="qr-tab-receive" value="receive" className="rounded-xl">{t.receiveTab}</TabsTrigger>
            <TabsTrigger data-testid="qr-tab-pay" value="pay" className="rounded-xl">{t.payTab}</TabsTrigger>
          </TabsList>

          <TabsContent value="receive" className="pt-4">
            <div className="flex flex-col items-center">
              <div className="p-5 bg-white rounded-3xl shadow-lg border border-border">
                <QRCodeSVG data-testid="personal-qr-code" value={qrValue} size={200} level="M" fgColor="#006C35" />
              </div>
              <p className="text-sm text-muted-foreground mt-4 text-center px-2">{t.qrReceiveDesc}</p>
              <div className="mt-3 flex items-center gap-2 bg-muted rounded-full ps-4 pe-1 py-1">
                <span className="font-bold text-primary">@{wallet?.handle}</span>
                <button
                  data-testid="qr-copy-handle"
                  onClick={copyHandle}
                  className="w-8 h-8 grid place-items-center rounded-full bg-background hover:bg-primary/10 transition-colors"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="pay" className="pt-4">
            <div className="space-y-3.5">
              <p className="text-sm text-muted-foreground">{t.scanToPayDesc}</p>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t.recipient}</label>
                <Input
                  data-testid="qr-pay-handle-input"
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  placeholder={t.recipientPlaceholder}
                  className="h-12 rounded-2xl mt-1.5"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground">{t.amount}</label>
                <div className="relative mt-1.5">
                  <Input
                    data-testid="qr-pay-amount-input"
                    type="number"
                    inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder={t.enterAmount}
                    className="h-12 rounded-2xl text-lg font-bold tabular-nums"
                  />
                  <span className="absolute top-1/2 -translate-y-1/2 end-4 text-sm font-bold text-muted-foreground">{t.currency}</span>
                </div>
              </div>
              <Button
                data-testid="qr-pay-submit"
                onClick={pay}
                disabled={loading || !handle || !amount}
                className="w-full h-[52px] rounded-2xl text-base font-bold"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : t.pay}
              </Button>
            </div>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
